import React, { useState } from 'react';
import { KeywordSuggestion, getKeywordSuggestions, addKeyword } from '@/api/seoApi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Lightbulb, Plus, RefreshCw, TrendingUp, BookOpen, ShoppingCart, Navigation, Users } from 'lucide-react';

interface KeywordSuggestionsProps {
  siteId: string;
  onKeywordAdded: () => void;
}

export function KeywordSuggestions({ siteId, onKeywordAdded }: KeywordSuggestionsProps) {
  const [suggestions, setSuggestions] = useState<KeywordSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingKeyword, setAddingKeyword] = useState<string | null>(null);

  const handleGenerateSuggestions = async () => {
    setLoading(true);
    try {
      const data = await getKeywordSuggestions(siteId, 20);
      setSuggestions(data);
    } catch (error) {
      console.error('Failed to generate suggestions:', error);
      alert('Failed to generate keyword suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddKeyword = async (keyword: string) => {
    setAddingKeyword(keyword);
    try {
      await addKeyword(siteId, keyword, {
        searchEngine: 'google',
        location: 'United States',
        device: 'desktop',
        checkFrequency: 'daily'
      });

      // Update the suggestion to mark it as tracked
      setSuggestions(prev =>
        prev.map(s =>
          s.keyword === keyword ? { ...s, alreadyTracked: true } : s
        )
      );

      onKeywordAdded();
    } catch (error) {
      console.error('Failed to add keyword:', error);
      alert('Failed to add keyword');
    } finally {
      setAddingKeyword(null);
    }
  };

  const getIntentIcon = (intent: string) => {
    switch (intent) {
      case 'commercial':
      case 'transactional':
        return <ShoppingCart className="h-4 w-4" />;
      case 'informational':
        return <BookOpen className="h-4 w-4" />;
      case 'navigational':
        return <Navigation className="h-4 w-4" />;
      case 'competitive':
        return <Users className="h-4 w-4" />;
      default:
        return <Lightbulb className="h-4 w-4" />;
    }
  };

  const getIntentBadge = (intent: string) => {
    const colors: Record<string, string> = {
      commercial: 'bg-green-500',
      transactional: 'bg-blue-500',
      informational: 'bg-purple-500',
      navigational: 'bg-orange-500',
      competitive: 'bg-yellow-500'
    };

    return (
      <Badge className={`${colors[intent] || 'bg-gray-500'} text-white`}>
        {intent}
      </Badge>
    );
  };

  const getDifficultyBadge = (difficulty: string) => {
    const colors: Record<string, string> = {
      easy: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      hard: 'bg-red-100 text-red-800'
    };

    return (
      <Badge variant="outline" className={colors[difficulty] || ''}>
        {difficulty}
      </Badge>
    );
  };

  const getScoreBadge = (score: number) => {
    if (score >= 80) {
      return <Badge className="bg-green-500">High Priority</Badge>;
    }
    if (score >= 60) {
      return <Badge className="bg-blue-500">Medium Priority</Badge>;
    }
    return <Badge variant="secondary">Low Priority</Badge>;
  };

  if (!loading && suggestions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Keyword Suggestions
          </CardTitle>
          <CardDescription>
            Get AI-powered keyword recommendations based on your site's content
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Lightbulb className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              Generate intelligent keyword suggestions based on your site's products, topics, and content.
            </p>
            <Button onClick={handleGenerateSuggestions} disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Lightbulb className="h-4 w-4 mr-2" />
                  Generate Suggestions
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Keyword Suggestions
          </CardTitle>
          <CardDescription>
            AI-generated keywords based on your site's content analysis
          </CardDescription>
        </div>
        <Button
          onClick={handleGenerateSuggestions}
          disabled={loading}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Regenerate
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 mx-auto animate-spin text-muted-foreground mb-2" />
            <p className="text-muted-foreground">Analyzing your site...</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Keyword</TableHead>
                <TableHead>Intent</TableHead>
                <TableHead>Difficulty</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Why This Keyword?</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {suggestions.map((suggestion, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {getIntentIcon(suggestion.intent)}
                      {suggestion.keyword}
                    </div>
                  </TableCell>
                  <TableCell>{getIntentBadge(suggestion.intent)}</TableCell>
                  <TableCell>{getDifficultyBadge(suggestion.difficulty)}</TableCell>
                  <TableCell>{getScoreBadge(suggestion.score)}</TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {suggestion.reason}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {suggestion.alreadyTracked ? (
                      <Badge variant="secondary">Already Tracking</Badge>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleAddKeyword(suggestion.keyword)}
                        disabled={addingKeyword === suggestion.keyword}
                      >
                        {addingKeyword === suggestion.keyword ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4 mr-2" />
                            Track
                          </>
                        )}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!loading && suggestions.length > 0 && (
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Tip:</strong> Start with high-priority, easy-difficulty keywords for quick wins.
              Commercial intent keywords tend to convert better, while informational keywords are easier to rank for.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
